import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { getQuizQuestions, submitQuiz, trackAnalyticsEvent } from '@/lib/api';
import { toast } from 'sonner';

const QuizPage = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const data = await getQuizQuestions();
      if (data && data.length > 0) {
        setQuestions(data);
      } else {
        setQuestions(getDefaultQuestions());
      }
      trackAnalyticsEvent('quiz_start', { source: 'web' });
    } catch (error) {
      console.error('Failed to load questions:', error);
      setQuestions(getDefaultQuestions());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultQuestions = () => [
    {
      id: 'q1',
      question_text: 'What would you most like to improve right now?',
      question_type: 'single_select',
      answers: [
        { answer_value: 'digestive', answer_label: 'Digestive health' },
        { answer_value: 'energy', answer_label: 'Energy & fatigue' },
        { answer_value: 'stress', answer_label: 'Stress & mood' },
        { answer_value: 'sleep', answer_label: 'Sleep quality' },
        { answer_value: 'immune', answer_label: 'Immune health' },
        { answer_value: 'hormone', answer_label: 'Hormone balance' },
        { answer_value: 'blood_sugar', answer_label: 'Blood sugar balance' },
        { answer_value: 'joint', answer_label: 'Joint comfort' },
        { answer_value: 'heart', answer_label: 'Heart health' },
        { answer_value: 'general', answer_label: 'General wellness' },
      ]
    },
    {
      id: 'q2',
      question_text: 'Which of these do you experience most often? (Select all that apply)',
      question_type: 'multi_select',
      answers: [
        { answer_value: 'bloating', answer_label: 'Bloating' },
        { answer_value: 'reflux', answer_label: 'Acid reflux' },
        { answer_value: 'constipation', answer_label: 'Constipation' },
        { answer_value: 'diarrhea', answer_label: 'Diarrhea' },
        { answer_value: 'fatigue', answer_label: 'Fatigue' },
        { answer_value: 'brain_fog', answer_label: 'Brain fog' },
        { answer_value: 'sugar_cravings', answer_label: 'Sugar cravings' },
        { answer_value: 'poor_sleep', answer_label: 'Poor sleep' },
        { answer_value: 'frequent_illness', answer_label: 'Frequent illness' },
        { answer_value: 'joint_pain', answer_label: 'Joint pain' },
        { answer_value: 'anxiety', answer_label: 'Anxiety' },
        { answer_value: 'mood_swings', answer_label: 'Mood swings' },
        { answer_value: 'headaches', answer_label: 'Headaches' },
        { answer_value: 'high_stress', answer_label: 'High stress' },
      ]
    },
    {
      id: 'q3',
      question_text: 'How is your digestion overall?',
      question_type: 'single_select',
      answers: [
        { answer_value: 'no_issues', answer_label: 'No issues' },
        { answer_value: 'occasional_bloating', answer_label: 'Occasional bloating' },
        { answer_value: 'frequent_bloating', answer_label: 'Frequent bloating' },
        { answer_value: 'reflux_heartburn', answer_label: 'Reflux / heartburn' },
        { answer_value: 'constipation', answer_label: 'Constipation' },
        { answer_value: 'loose_stools', answer_label: 'Loose stools' },
      ]
    },
    {
      id: 'q4',
      question_text: 'How are your energy levels during the day?',
      question_type: 'single_select',
      answers: [
        { answer_value: 'consistent', answer_label: 'Consistent energy' },
        { answer_value: 'afternoon_slump', answer_label: 'Afternoon slump' },
        { answer_value: 'low_most_day', answer_label: 'Low most of the day' },
        { answer_value: 'exhausted', answer_label: 'Exhausted despite sleep' },
      ]
    },
    {
      id: 'q5',
      question_text: 'How would you describe your stress level?',
      question_type: 'single_select',
      answers: [
        { answer_value: 'low', answer_label: 'Low' },
        { answer_value: 'moderate', answer_label: 'Moderate' },
        { answer_value: 'high', answer_label: 'High' },
        { answer_value: 'constant', answer_label: 'Constant stress' },
      ]
    },
    {
      id: 'q6',
      question_text: 'How well do you sleep?',
      question_type: 'single_select',
      answers: [
        { answer_value: 'sleep_well', answer_label: 'Sleep well' },
        { answer_value: 'trouble_falling', answer_label: 'Trouble falling asleep' },
        { answer_value: 'wake_during', answer_label: 'Wake during the night' },
        { answer_value: 'wake_tired', answer_label: 'Wake up tired' },
      ]
    },
    {
      id: 'q7',
      question_text: 'Do you experience joint or body discomfort?',
      question_type: 'single_select',
      answers: [
        { answer_value: 'none', answer_label: 'None' },
        { answer_value: 'occasional_stiffness', answer_label: 'Occasional stiffness' },
        { answer_value: 'frequent_joint_pain', answer_label: 'Frequent joint pain' },
        { answer_value: 'inflammation', answer_label: 'Inflammation flare-ups' },
      ]
    },
    {
      id: 'q8',
      question_text: 'How often do you get sick?',
      question_type: 'single_select',
      answers: [
        { answer_value: 'rarely', answer_label: 'Rarely' },
        { answer_value: 'few_times_year', answer_label: 'A few times per year' },
        { answer_value: 'frequently', answer_label: 'Frequently' },
        { answer_value: 'chronic', answer_label: 'Chronic infections' },
      ]
    },
  ];

  const totalSteps = questions.length + 1;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const isEmailStep = currentStep === questions.length;
  const currentQuestion = questions[currentStep];

  const handleAnswerSelect = (answerValue) => {
    if (!currentQuestion) return;
    
    const questionId = currentQuestion.id;
    const isMultiSelect = currentQuestion.question_type === 'multi_select';
    
    if (isMultiSelect) {
      const current = answers[questionId] || [];
      if (current.includes(answerValue)) {
        setAnswers({
          ...answers,
          [questionId]: current.filter(v => v !== answerValue)
        });
      } else {
        setAnswers({
          ...answers,
          [questionId]: [...current, answerValue]
        });
      }
    } else {
      setAnswers({
        ...answers,
        [questionId]: [answerValue]
      });
    }
  };

  const isAnswerSelected = (answerValue) => {
    if (!currentQuestion) return false;
    const questionAnswers = answers[currentQuestion.id] || [];
    return questionAnswers.includes(answerValue);
  };

  const canProceed = () => {
    if (isEmailStep) return true;
    if (!currentQuestion) return false;
    const questionAnswers = answers[currentQuestion.id] || [];
    return questionAnswers.length > 0;
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await submitQuiz(answers, email || null);
      sessionStorage.setItem('quizResult', JSON.stringify(result));
      navigate('/results');
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      toast.error('Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center" data-testid="quiz-loading">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-[#d4e4bc] mx-auto animate-pulse" />
          <p className="mt-4 text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex flex-col" data-testid="quiz-page">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-600 hover:text-[#1a1a1a] transition-colors"
              data-testid="quiz-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm">Back</span>
            </button>
            <span className="font-serif text-lg text-[#1a1a1a]">Supplement Finder</span>
            <span className="text-sm text-gray-500">
              {currentStep + 1} of {totalSteps}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </header>

      {/* Quiz Content */}
      <main className="flex-1 flex items-center py-12">
        <div className="max-w-2xl mx-auto px-6 w-full">
          {isEmailStep ? (
            <div className="animate-fade-in text-center">
              <div className="w-16 h-16 rounded-full bg-[#d4e4bc] flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[#1a1a1a]" strokeWidth={1.5} />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-[#1a1a1a] mb-4">
                Almost there!
              </h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto text-sm">
                Enter your email to receive your personalized supplement plan and 
                exclusive wellness tips. (Optional)
              </p>
              <div className="max-w-sm mx-auto mb-8">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-14 text-center rounded-md border-gray-200 focus:border-[#1a1a1a] focus:ring-[#1a1a1a]"
                  data-testid="email-input"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="btn-primary px-10 py-4"
                  data-testid="submit-quiz-btn"
                >
                  {submitting ? 'Processing...' : 'Get My Results'}
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="text-gray-500"
                  data-testid="skip-email-btn"
                >
                  Skip & View Results
                </Button>
              </div>
            </div>
          ) : currentQuestion ? (
            <div className="animate-fade-in">
              <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-4 text-center">
                Question {currentStep + 1}
              </p>
              <h2 className="font-serif text-2xl md:text-3xl text-[#1a1a1a] mb-8 text-center">
                {currentQuestion.question_text}
              </h2>
              
              {currentQuestion.question_type === 'multi_select' && (
                <p className="text-sm text-gray-500 mb-6 text-center">
                  Select all that apply
                </p>
              )}

              <div className="grid gap-3 mb-8">
                {currentQuestion.answers?.map((answer, index) => (
                  <button
                    key={answer.answer_value}
                    onClick={() => handleAnswerSelect(answer.answer_value)}
                    className={`quiz-option flex items-center gap-4 text-left ${
                      isAnswerSelected(answer.answer_value) ? 'selected' : ''
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`answer-${answer.answer_value}`}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isAnswerSelected(answer.answer_value)
                        ? 'border-[#1a1a1a] bg-[#1a1a1a]'
                        : 'border-gray-300'
                    }`}>
                      {isAnswerSelected(answer.answer_value) && (
                        <Check className="w-4 h-4 text-white" strokeWidth={2} />
                      )}
                    </div>
                    <span className="text-[#1a1a1a]">{answer.answer_label}</span>
                  </button>
                ))}
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="btn-primary px-10"
                  data-testid="next-question-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </main>

      {/* Footer Disclaimer */}
      <footer className="py-4 border-t border-gray-200 bg-white">
        <p className="text-xs text-gray-400 text-center max-w-2xl mx-auto px-6">
          This quiz is for educational purposes only and is not medical advice. 
          Please consult your healthcare practitioner before beginning any nutritional program.
        </p>
      </footer>
    </div>
  );
};

export default QuizPage;
